/**
 * Profile operations hook.
 * Handles profile updates and language settings.
 */

import { useCallback } from 'react'
import type { TokenManager } from '@/services/token-manager'
import { authService, type AuthService } from '@/services/auth.service'
import { type User, type LanguageSettings, AuthError, normalizeUserData } from './types'
import type { AvatarConfig } from '@/types/avatar'
import { TARGET_LANG_CODES } from '@/constants/languages'

interface UseProfileOperationsParams {
    user: User | null
    tokenManager: TokenManager
    setUser: React.Dispatch<React.SetStateAction<User | null>>
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    setError: React.Dispatch<React.SetStateAction<string | null>>
    /** Auth service instance (defaults to authService singleton) */
    authService?: AuthService
}

interface UseProfileOperationsReturn {
    updateProfile: (updates: {
        display_name?: string | null
        avatar_url?: string | null
        avatar_config?: AvatarConfig | null
    }) => Promise<User>
    patchProfile: (updates: {
        display_name?: string | null
        avatar_url?: string | null
        avatar_config?: AvatarConfig | null
    }) => Promise<User>
    updateLanguageSettings: (settings: LanguageSettings) => Promise<User>
}

/**
 * Hook for profile update operations.
 */
export const useProfileOperations = ({
    user,
    tokenManager,
    setUser,
    setLoading,
    setError,
    authService: authSvc = authService,
}: UseProfileOperationsParams): UseProfileOperationsReturn => {
    // Use the provided service or default to singleton
    const service: AuthService = authSvc ?? authService
    const updateProfile = useCallback(
        async (updates: {
            display_name?: string | null
            avatar_url?: string | null
            avatar_config?: AvatarConfig | null
        }) => {
            if (!user) {
                throw new AuthError('No user logged in', 'errors.auth.notLoggedIn')
            }

            setLoading(true)
            setError(null)
            try {
                // Use authService - authenticatedFetch handles token management
                const data = await service.updateProfile(updates)

                if (!data || typeof data !== 'object') {
                    throw new Error('Invalid response data from server')
                }

                const normalizedUserData = normalizeUserData(data, TARGET_LANG_CODES)

                // Update user state and storage
                setUser(normalizedUserData)
                tokenManager.setUserInStorage(normalizedUserData)

                return normalizedUserData
            } catch (error) {
                console.error('Profile update error:', error)
                const errorMessage =
                    error instanceof Error ? error.message : 'Profile update failed'
                setError(errorMessage)
                throw error
            } finally {
                setLoading(false)
            }
        },
        [user, tokenManager, setUser, setLoading, setError, service]
    )

    const patchProfile = useCallback(
        async (updates: {
            display_name?: string | null
            avatar_url?: string | null
            avatar_config?: AvatarConfig | null
        }) => {
            if (!user) {
                throw new AuthError('No user logged in', 'errors.auth.notLoggedIn')
            }

            setLoading(true)
            setError(null)
            try {
                // Use patchProfile method which uses PATCH HTTP method - authenticatedFetch handles token management
                const data = await service.patchProfile(updates)

                if (!data || typeof data !== 'object') {
                    throw new Error('Invalid response data from server')
                }

                const normalizedUserData = normalizeUserData(data, TARGET_LANG_CODES)

                // Update user state and storage
                setUser(normalizedUserData)
                tokenManager.setUserInStorage(normalizedUserData)

                return normalizedUserData
            } catch (error) {
                console.error('Profile patch error:', error)
                const errorMessage =
                    error instanceof Error ? error.message : 'Profile update failed'
                setError(errorMessage)
                throw error
            } finally {
                setLoading(false)
            }
        },
        [user, tokenManager, setUser, setLoading, setError, service]
    )

    const updateLanguageSettings = useCallback(
        async (settings: LanguageSettings) => {
            if (!user) {
                throw new AuthError('No user logged in', 'errors.auth.notLoggedIn')
            }

            setLoading(true)
            setError(null)
            try {
                // Map LanguageSettings to UpdateLanguageSettingsRequest format
                // Backend expects primary_target_lang, not target_lang
                const request = {
                    target_langs: settings.target_langs,
                    primary_target_lang: settings.primary_target_lang,
                    ...(settings.user_lang && { user_lang: settings.user_lang }),
                }
                // authenticatedFetch handles token management
                const data = await service.updateLanguageSettings(request)

                // Normalize target_langs with filtering
                const targetLangs =
                    Array.isArray(data.target_langs) && data.target_langs.length > 0
                        ? data.target_langs
                        : []
                const filtered = targetLangs.filter((code: string) =>
                    TARGET_LANG_CODES.includes(code)
                )

                // Update local user state with new language settings
                if (!user) {
                    throw new Error('User state is null during language settings update')
                }

                const updatedUser: User = {
                    ...user,
                    user_lang: data.user_lang || user.user_lang,
                    target_langs: filtered,
                }

                tokenManager.setUserInStorage(updatedUser)
                setUser(updatedUser)

                // Return the updated user
                return updatedUser
            } catch (error) {
                console.error('Language settings update error:', error)
                const errorMessage =
                    error instanceof Error ? error.message : 'Language settings update failed'
                setError(errorMessage)
                throw error
            } finally {
                setLoading(false)
            }
        },
        [user, tokenManager, setUser, setLoading, setError, service]
    )

    return {
        updateProfile,
        patchProfile,
        updateLanguageSettings,
    }
}
